import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { Parent } from "../../types/models";
import ParentFormFields from "./ParentFormFields";
import { useParentForm } from "@/hooks/useParentForm";

interface EditParentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  parent: Parent | null;
  onSubmit: (parentId: string, updatedData: Partial<Parent>) => void;
  onDelete?: (parent: Parent) => void;
}

const EditParentDialog: React.FC<EditParentDialogProps> = ({
  isOpen,
  onClose,
  parent,
  onSubmit,
  onDelete,
}) => {
  // Custom hook to handle parent form logic
  const {
    formData,
    isSubmitting,
    handleChange,
    handleSwitchChange,
    handleImageUpload,
    saveParent
  } = useParentForm({ 
    parent, 
    onUpdated: (parentId, updatedData) => {
      onSubmit(parentId, updatedData);
      onClose();
    }
  });

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveParent();
  };

  if (!parent) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-6 py-4 border-b bg-gradient-to-r from-pink-50 to-purple-50 flex-shrink-0">
          <DialogTitle className="text-xl font-semibold text-gray-900">Edit Parent</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {`${parent.first_name} ${parent.last_name}`}
          </p>
        </DialogHeader>
        
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <form id="edit-parent-form" onSubmit={handleFormSubmit}>
              <ParentFormFields
                formData={formData}
                handleChange={handleChange}
                handleSwitchChange={handleSwitchChange}
                handleImageUpload={handleImageUpload}
                onDelete={onDelete}
                parent={parent}
              />
            </form>
          </div>
          
          <div className="px-6 py-4 border-t bg-white/80 backdrop-blur-sm flex justify-end gap-3 flex-shrink-0 sticky bottom-0">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              form="edit-parent-form" 
              type="submit" 
              disabled={isSubmitting}
              className="bg-pink-600 hover:bg-pink-700"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving Changes...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditParentDialog;
